import React, { useState, useEffect, useMemo } from 'react';
import { View, TouchableOpacity } from 'react-native';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation } from '@tanstack/react-query';
import moment from 'moment';
import { useToast } from 'react-native-toast-notifications';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import Icon from '../../../../components/IcomoonIcon';
import BottomSheet from '../../../../components/BottomSheet';
import Input from '../../../../components/Forms/Input';
import IconButton from '../../../../components/IconButton';
import { MonthSelectorDropdown, MonthSelector } from '../../../../components/Forms/MonthSelector';
import api from '../../../../utils/api';

interface AssignFeeToUnitModalProps {
  visible: boolean;
  unit: any;
  communityId: string;
  onRequestClose: () => void;
  onFeeCreated: () => void;
}

interface FeeType {
  uuid: string;
  type: 'main' | 'other';
  name: string;
  amount: number;
  is_variant: boolean;
}

function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export default function AssignFeeToUnitModal({
  visible,
  unit,
  communityId,
  onRequestClose,
  onFeeCreated,
}: AssignFeeToUnitModalProps) {
  // Hooks
  const toast = useToast();

  // State
  const [selectedFeeType, setSelectedFeeType] = useState<FeeType | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [showMonthSelector, setShowMonthSelector] = useState(false);

  // Form management
  const { control, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      fee_type_id: '',
      amount: '',
      description: '',
    }
  });

  // Fetch fee types
  const { data: feeTypes, isLoading } = useQuery({
    queryKey: ['community.fees.types', communityId],
    queryFn: () => api.get(`/communities/${communityId}/fees/types`)
      .then(response => response.data),
    enabled: visible,
  });

  // Fetch existing unit fees para filtrar meses duplicados
  const { data: unitFees } = useQuery({
    queryKey: ['unit.fees', communityId, unit?.uuid],
    queryFn: () => api.get(`/communities/${communityId}/units/${unit?.uuid}`)
      .then(res => res.data.unit_fees || []),
    enabled: visible && !!unit?.uuid,
  });

  // Calcular meses con adeudos de mantenimiento existentes
  const assignedMonths = useMemo(() => {
    if (!unitFees) return [];
    return unitFees
      .filter((uf: any) => uf.fee?.type?.type === 'main')
      .map((uf: any) => moment(uf.apply_month).format('YYYY-MM'));
  }, [unitFees]);

  // Pre-seleccionar fee_type de la vivienda si existe
  useEffect(() => {
    if (unit?.fee_type && feeTypes) {
      const unitFeeType = feeTypes.find((ft: FeeType) => ft.uuid === unit.fee_type.uuid);
      if (unitFeeType) {
        setSelectedFeeType(unitFeeType);
        setValue('fee_type_id', unitFeeType.uuid);
        setValue('amount', unitFeeType.amount.toString());
      }
    }
  }, [unit, feeTypes]);

  // Auto-generar descripción para fees tipo "main"
  useEffect(() => {
    if (selectedFeeType?.type === 'main' && selectedMonth) {
      const monthName = capitalize(
        selectedMonth.toLocaleString('es', { month: 'long' })
      );
      const year = selectedMonth.getFullYear();
      setValue('description', `${selectedFeeType.name} ${monthName} ${year}`);
    }
  }, [selectedMonth, selectedFeeType]);

  // Mutation para crear adeudo
  const createFeeUnit = useMutation({
    mutationFn: (data: any) =>
      api.post(`/communities/${communityId}/units/${unit.uuid}/fees`, data),
    onSuccess: () => {
      toast.show('Adeudo registrado', { type: 'success' });
      onFeeCreated();
    },
    onError: (error: any) => {
      toast.show(error.response?.data?.message || 'Error al registrar adeudo', {
        type: 'danger'
      });
    },
  });

  const onSubmit = (data: any) => {
    const payload: any = {
      fee_type_id: selectedFeeType?.uuid,
      amount: parseFloat(data.amount),
      description: data.description,
    };

    if (selectedFeeType?.type === 'main') {
      payload.apply_month = moment(selectedMonth).format('YYYY-MM-DD');
    }

    createFeeUnit.mutate(payload);
  };

  return (
    <BottomSheet
      visible={visible}
      onRequestClose={onRequestClose}
      height={selectedFeeType?.type === 'main' ?  (showMonthSelector ? 750 : 600) : 550}
    >
      <View style={tw`pt-5 px-5`}>
        {/* Header */}
        <View style={tw`mb-4`}>
          <Text style={tw`text-lg font-600 text-secondary`}>Asignar nueva cuota</Text>
          <TouchableOpacity
            style={tw`absolute -top-0 right-2 items-center justify-center`}
            onPress={onRequestClose}
          >
            <Icon name="close" style={tw`text-red`} size={26} />
          </TouchableOpacity>
        </View>

        {/* FeeType Selector */}
        <FeeTypeSelector
          feeTypes={feeTypes?.filter((ft: FeeType) => !ft.is_variant)}
          selectedFeeType={selectedFeeType}
          onSelect={(feeType: FeeType) => {
            setSelectedFeeType(feeType);
            setValue('fee_type_id', feeType.uuid);
            setValue('amount', feeType.amount.toString());
          }}
        />

        {/* Campos condicionales según tipo */}
        {selectedFeeType && (
          <>
            {/* Month Selector (solo para tipo "main") */}
            {selectedFeeType.type === 'main' && (
              <View style={tw`px-6 mb-4`}>
                <Text style={tw`text-gray-600 mb-2`}>Mes</Text>
                <MonthSelectorDropdown
                  selectedDate={selectedMonth}
                  onPress={() => setShowMonthSelector(true)}
                />
                {showMonthSelector && (
                  <MonthSelector
                    selectedDate={selectedMonth}
                    minDate={moment().subtract(5, 'year')}
                    maxDate={moment().add(5, 'year')}
                    onChange={(date: Date) => {
                      // Validar si el mes ya está asignado
                      const monthKey = moment(date).format('YYYY-MM');
                      if (assignedMonths.includes(monthKey)) {
                        toast.show('Este mes ya tiene un adeudo de mantenimiento asignado', {
                          type: 'warning'
                        });
                        return;
                      }
                      setSelectedMonth(date);
                      setShowMonthSelector(false);
                    }}
                  />
                )}
                {/* Advertencia si el mes seleccionado ya está asignado */}
                {assignedMonths.includes(moment(selectedMonth).format('YYYY-MM')) && (
                  <Text style={tw`text-red-500 text-xs mt-1`}>
                    ⚠️ Este mes ya tiene un adeudo de mantenimiento
                  </Text>
                )}
              </View>
            )}

            {/* Amount */}
            <View style={tw`px-6 mb-4`}>
              <Text style={tw`text-gray-600 mb-2`}>Cantidad</Text>
              <View style={tw`row items-center relative`}>
                <Input
                  control={control}
                  name="amount"
                  placeholder="0.00"
                  keyboardType="numeric"
                  style={{ input: tw`pl-8` }}
                  rules={{ required: true }}
                />
                <View style={tw`absolute left-4 top-0 h-12 justify-center`} pointerEvents="none">
                  <Text style={tw`text-gray-500`}>$</Text>
                </View>
              </View>
            </View>

            {/* Description */}
            <View style={tw`px-6 mb-4`}>
              <Text style={tw`text-gray-600 mb-2`}>Descripción</Text>
              {selectedFeeType.type === 'main' ? (
                <Text style={tw`text-gray-400 italic`}>
                  {watch('description')}
                </Text>
              ) : (
                <Input
                  control={control}
                  name="description"
                  placeholder="ej: Multa por ruido excesivo"
                  rules={{ required: true, minLength: 3 }}
                />
              )}
            </View>

            {/* Submit Button */}
            <View style={tw`px-6 mt-4`}>
              <IconButton
                title={createFeeUnit.isPending ? 'Asignando...' : 'Asignar nueva cuota'}
                onPress={handleSubmit(onSubmit)}
                disabled={createFeeUnit.isPending}
              />
            </View>
          </>
        )}
      </View>
    </BottomSheet>
  );
}

// Sub-componente FeeTypeSelector
function FeeTypeSelector({
  feeTypes,
  selectedFeeType,
  onSelect
}: {
  feeTypes: FeeType[] | undefined;
  selectedFeeType: FeeType | null;
  onSelect: (feeType: FeeType) => void;
}) {
  if (!feeTypes || feeTypes.length === 0) {
    return (
      <View style={tw`px-6 mb-4`}>
        <Text style={tw`text-gray-400`}>Cargando tipos de cuota...</Text>
      </View>
    );
  }

  return (
    <View style={tw`px-6 mb-4`}>
      <Text style={tw`text-gray-600 mb-2`}>Tipo de Cuota</Text>
      {feeTypes?.map((feeType) => {
        const isSelected = selectedFeeType?.uuid === feeType.uuid;
        const color = feeType.type === 'main' ? 'secondary' : 'primary';

        return (
          <TouchableOpacity
            key={feeType.uuid}
            onPress={() => onSelect(feeType)}
            style={tw.style(
              `px-4 py-3 mb-2 rounded-lg bg-gray-100`,
              isSelected && `border border-${color}`
            )}
          >
            <View style={tw`row justify-between items-center`}>
              <View>
                <Text style={tw`font-medium`}>{feeType.name}</Text>
                <Text style={tw`text-gray-500 text-sm`}>
                  ${feeType.amount.toFixed(2)}
                </Text>
              </View>
              <View style={tw`h-5 w-5 rounded-full border border-gray-300 items-center justify-center`}>
                {isSelected && (
                  <View style={tw`h-3 w-3 rounded-full bg-${color}`} />
                )}
              </View>
            </View>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}
